import { useEffect, useMemo, useRef, useState } from "react";
import { Clock3, Search, Sparkles, Tag } from "lucide-react";
import { getTemplateById, getTemplates, TemplateDefinition } from "@/config/templates";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";

interface TemplatePickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (
    templateId: string,
    options?: { setAsDefault?: boolean; actionOverride?: "new" | "import" | null },
  ) => void;
  recentTemplateIds?: string[];
  defaultTemplateId?: string | null;
  onSetDefault?: (templateId: string | null) => void;
  onClearRecents?: () => void;
  action?: "new" | "import" | null;
}

const TemplateCard = ({
  template,
  onSelect,
  isDefault,
}: {
  template: TemplateDefinition;
  onSelect: (templateId: string) => void;
  isDefault?: boolean;
}) => {
  const preview = useMemo(
    () =>
      template.defaultContent
        .replace(/\n+/g, "\n")
        .split("\n")
        .filter(Boolean)
        .slice(0, 4)
        .join("\n"),
    [template.defaultContent],
  );

  const metadataCount = Object.keys(template.defaultMetadata || {}).length;
  const fieldCount = template.defaultFields?.length ?? 0;

  return (
    <button
      className="group w-full text-left"
      onClick={() => onSelect(template.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(template.id);
        }
      }}
    >
      <div
        className={`h-full rounded-lg border bg-card p-4 transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
          isDefault
            ? "border-primary shadow-[0_0_0_1px_rgba(14,165,233,0.35)]"
            : "border-border"
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              {template.defaultMetadata?.category || "Template"}
            </div>
            <h3 className="text-lg font-semibold leading-tight">{template.name}</h3>
            <p className="text-sm text-muted-foreground">{template.description}</p>
            {template.defaultMetadata && (
              <div className="flex flex-wrap gap-2 pt-1">
                {Object.entries(template.defaultMetadata).map(([key, value]) => (
                  <Badge key={key} variant="secondary" className="rounded-full text-xs">
                    <span className="font-medium capitalize">{key}:</span> {String(value)}
                  </Badge>
                ))}
              </div>
            )}
            {isDefault && (
              <div className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary">
                <Sparkles className="h-3 w-3" /> Default
              </div>
            )}
          </div>
          {(metadataCount > 0 || fieldCount > 0) && (
            <div className="flex flex-col items-end gap-1">
              {metadataCount > 0 && (
                <Badge variant="outline" className="shrink-0">
                  {metadataCount} metadata {metadataCount === 1 ? "value" : "values"}
                </Badge>
              )}
              {fieldCount > 0 && (
                <Badge variant="outline" className="shrink-0">
                  {fieldCount} {fieldCount === 1 ? "field" : "fields"}
                </Badge>
              )}
            </div>
          )}
        </div>
        <div className="mt-3 space-y-2 rounded-md border border-dashed border-border bg-muted/50 p-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground/80">
            <Search className="h-3.5 w-3.5" />
            Preview
          </div>
          <p className="whitespace-pre-wrap leading-relaxed line-clamp-4">{preview}</p>
        </div>
      </div>
    </button>
  );
};

const TemplatePickerModal = ({
  open,
  onOpenChange,
  onSelect,
  recentTemplateIds,
  defaultTemplateId,
  onSetDefault,
  onClearRecents,
  action,
}: TemplatePickerModalProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [setAsDefault, setSetAsDefault] = useState(false);
  const actionLabel = action === "import" ? "Import" : "Create";

  const templates = useMemo(() => getTemplates(), []);
  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          templates
            .map((template) => template.defaultMetadata?.category)
            .filter((category): category is string => Boolean(category)),
        ),
      ).sort(),
    [templates],
  );

  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");
    setIsMobile(media.matches);
    const handleChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    if (open) {
      const timer = window.setTimeout(() => {
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }, 150);

      return () => window.clearTimeout(timer);
    }

    setSetAsDefault(false);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!searchTerm && defaultTemplateId) {
      const defaultTemplate = getTemplateById(defaultTemplateId);
      if (defaultTemplate) {
        setSearchTerm(defaultTemplate.name);
      }
    }
  }, [defaultTemplateId, open, searchTerm]);

  const filteredTemplates = templates.filter((template) => {
    const matchesCategory = activeCategory ? template.defaultMetadata?.category === activeCategory : true;
    const metadataSearch = template.defaultMetadata
      ? Object.entries(template.defaultMetadata)
          .map(([key, value]) => `${key}: ${String(value)}`)
          .join(" ")
      : "";
    const searchTarget = `${template.name} ${template.description} ${template.defaultContent} ${metadataSearch}`.toLowerCase();
    const matchesSearch = searchTarget.includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleSelect = (templateId: string) => {
    onSelect(templateId, { setAsDefault });
    const foundTemplate = getTemplateById(templateId);
    if (foundTemplate) {
      setSearchTerm(foundTemplate.name);
    }
    onOpenChange(false);
  };

  const recentTemplates = (recentTemplateIds || [])
    .map((templateId) => getTemplateById(templateId))
    .filter((template): template is TemplateDefinition => Boolean(template));

  const defaultTemplate = defaultTemplateId ? getTemplateById(defaultTemplateId) : undefined;

  const personalizationSection = (
    <div className="space-y-4 rounded-lg border border-border bg-muted/40 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="h-4 w-4 text-primary" /> Personalized picks
          </div>
          <p className="text-xs text-muted-foreground">
            Quickly reuse your go-to templates or set a default for faster starts.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={setAsDefault} onCheckedChange={(value) => setSetAsDefault(value === true)} />
            <span className="whitespace-nowrap">Set as default</span>
          </div>
          {defaultTemplateId && (
            <Button variant="ghost" size="sm" onClick={() => onSetDefault?.(null)}>
              Clear default
            </Button>
          )}
          {recentTemplates.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => onClearRecents?.()}>
              Clear recents
            </Button>
          )}
        </div>
      </div>

      {defaultTemplate && (
        <div className="flex flex-col gap-3 rounded-md border border-dashed border-primary/30 bg-primary/5 p-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm font-semibold text-primary">
              <Sparkles className="h-4 w-4" /> Default template
            </div>
            <div>
              <p className="text-sm font-semibold leading-tight">{defaultTemplate.name}</p>
              <p className="text-xs text-muted-foreground">{defaultTemplate.description}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary">
              <Clock3 className="h-3 w-3" /> Preferred
            </span>
            <Button size="sm" onClick={() => handleSelect(defaultTemplate.id)}>
              Use default
            </Button>
            <Button variant="outline" size="sm" onClick={() => onSetDefault?.(null)}>
              Remove default
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">Recents</p>
          {recentTemplates.length > 0 ? (
            <span className="text-xs text-muted-foreground">Last {recentTemplates.length} selections</span>
          ) : null}
        </div>
        {recentTemplates.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {recentTemplates.map((template) => (
              <Button
                key={template.id}
                variant={template.id === defaultTemplateId ? "default" : "secondary"}
                size="sm"
                className="rounded-full"
                onClick={() => handleSelect(template.id)}
              >
                {template.name}
                {template.id === defaultTemplateId && (
                  <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold">
                    <Sparkles className="h-3 w-3" />
                  </span>
                )}
              </Button>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Your recent templates will show up here.</p>
        )}
      </div>
    </div>
  );

  const Content = (
    <div className="space-y-4">
      {personalizationSection}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search templates by name, description, metadata, or content"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant={activeCategory === null ? "default" : "outline"}
            className="cursor-pointer"
            onClick={() => setActiveCategory(null)}
          >
            <Tag className="mr-1.5 h-3.5 w-3.5" /> All templates
          </Badge>
          {categories.map((category) => (
            <Badge
              key={category}
              variant={activeCategory === category ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </Badge>
          ))}
          {searchTerm && <Button variant="ghost" size="sm" onClick={() => setSearchTerm("")}>Clear search</Button>}
        </div>
      </div>

      <ScrollArea className="max-h-[60vh] pr-1">
        <div className="grid gap-3 md:grid-cols-2">
          {filteredTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onSelect={handleSelect}
              isDefault={template.id === defaultTemplateId}
            />
          ))}
        </div>
        {filteredTemplates.length === 0 && (
          <p className="py-10 text-center text-sm text-muted-foreground">No templates match your search. Try another term.</p>
        )}
      </ScrollArea>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-[90vh] overflow-hidden sm:max-w-2xl">
          <SheetHeader className="text-left">
            <SheetTitle>{action ? `${actionLabel} with a template` : "Choose a template"}</SheetTitle>
            <SheetDescription>
              {action
                ? `${actionLabel} the right layout for your workflow. Filter by category or search metadata to find the best fit.`
                : "Pick a starting point before creating or importing."}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4">{Content}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{action ? `${actionLabel} with a template` : "Choose a template"}</DialogTitle>
          <DialogDescription>
            {action
              ? `${actionLabel} the right layout for your workflow. Filter by category or search metadata to find the best fit.`
              : "Pick a starting point before creating or importing."}
          </DialogDescription>
        </DialogHeader>
        {Content}
      </DialogContent>
    </Dialog>
  );
};

export default TemplatePickerModal;
